import { dsClass } from '../../common/constants/dsClasses.js';
import { t } from '../../common/i18n/index.js';
import { isAdminUser } from '../../common/utils/accountUtils.js';
import { isFeatureEnabledForUser, isStrictFeatureEnabled } from '../../common/utils/featureFlagUtils.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useFeatureFlags } from '../../public/site/ConfigProvider.js';
import { Grid } from '../Grid/Grid.js';
import { LinkableAccordion } from '../LinkableAccordion/index.js';
import { MfaSetting } from '../MFA/MfaSetting.js';
import { CallerDisplayNameSetting } from './CallerDisplayNameSetting.js';
import { InvoiceDueSmsReminderSetting, anyCompanyHasSmsReminderSettingVisible } from './InvoiceDueSmsReminderSetting';
import { KelloSimBillingModelSetting } from './KelloSimBillingModelSetting.js';
import type { CompanyInfoLoaderData } from '../../common/loaders';
import type { MfaDetailsResponse } from '../../generated/api/mfaDetailsResponse.js';

import './CompanySettings.scss';

export interface CompanySettingsProps {
  companyInfos?: CompanyInfoLoaderData[];
  mfaDetails: MfaDetailsResponse;
}

export const CompanySettings = ({ companyInfos, mfaDetails }: CompanySettingsProps) => {
  const { authenticatedUser } = useAuth();
  const featureFlags = useFeatureFlags();
  const callerDisplayNameEnabled =
    isAdminUser(authenticatedUser) &&
    isFeatureEnabledForUser('callerDisplayName', featureFlags, authenticatedUser?.enabledFeatureFlags);
  const isKelloSimBillingModelEnabled = isStrictFeatureEnabled(featureFlags.kelloSimBillingModel);

  return (
    <div className={dsClass.MARGIN_TOP_6}>
      <Grid>
        <h3 className={dsClass.DESCRIPTION_TITLE}>{t.EJQF('Settings')}</h3>
        <LinkableAccordion
          heading={t.QQFY('Two-factor authentication')}
          id="mfa-setting"
          defaultOpen={true}
          contentClasses="of-company-settings__accordion-content"
        >
          <MfaSetting mfaDetails={mfaDetails} />
        </LinkableAccordion>
        {companyInfos && anyCompanyHasSmsReminderSettingVisible(companyInfos) && (
          <LinkableAccordion
            heading={t.T4MT('Block invoice due SMS-reminder')}
            id="block-invoice-due-reminder-setting"
            defaultOpen={true}
            contentClasses="of-company-settings__accordion-content"
          >
            <InvoiceDueSmsReminderSetting companyInfos={companyInfos} />
          </LinkableAccordion>
        )}
        {callerDisplayNameEnabled && (
          <LinkableAccordion
            heading={t.PITO('Information on the Elisa Kuka soittaa service')}
            id="caller-display-name-setting"
            defaultOpen={true}
          >
            <div className={dsClass.PADDING_BOTTOM_8}>
              <CallerDisplayNameSetting />
            </div>
          </LinkableAccordion>
        )}
        {companyInfos && isKelloSimBillingModelEnabled && (
          <LinkableAccordion
            heading="KelloSIM"
            id="kellosim-billing-model-setting"
            defaultOpen={true}
            contentClasses="of-company-settings__accordion-content"
          >
            <KelloSimBillingModelSetting companyInfos={companyInfos} />
          </LinkableAccordion>
        )}
      </Grid>
    </div>
  );
};
